import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCopy, FiCheckCircle, FiMail, FiExternalLink } = FiIcons;

const ReferralBanner = ({ id, type = 'partner', name = '' }) => {
  const [copied, setCopied] = useState(false);
  
  // Sikrer at vi har en gyldig ID, ellers viser vi en placeholder
  const displayId = id || (type === 'partner' ? 'ID-0000' : 'SELGER-0000');
  
  // Bygg lenken - bruker hash-router format som er standard i appen
  const baseUrl = window.location.origin;
  const referralLink = `${baseUrl}/#/company/register?ref=${displayId}`;
  
  const label = type === 'partner' ? 'Partner ID' : 'Selger ID';
  const title = type === 'partner' ? 'Din Verve-lenke' : 'Din Salgslenke';
  const desc = type === 'partner' 
    ? 'Del denne lenken med kunder for å registrere provisjon.'
    : 'Bruk denne lenken for å sikre at salget krediteres deg.';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareEmail = () => {
    const subject = `Invitasjon til HMSVarsling`;
    const body = `Hei,\n\nHer er lenken for registrering hos HMSVarsling:\n${referralLink}\n\nMvh,\n${name}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-[24px] p-6 md:p-8 text-white shadow-xl mb-10 relative overflow-hidden border border-white/10"
    >
      {/* Bakgrunnsdekor */}
      <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-8 relative z-10">
        <div className="flex-1 space-y-6 min-w-0">
          <div>
            <h2 className="text-2xl font-black mb-2 tracking-tight flex items-center gap-3">
              {title}
              <span className="bg-green-500/20 text-green-300 text-[10px] px-2 py-1 rounded-md uppercase tracking-widest font-bold border border-green-500/30">Aktiv</span>
            </h2>
            <p className="text-blue-100 text-sm font-medium max-w-xl">{desc}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex items-center gap-3 bg-black/20 p-2 pl-4 rounded-xl backdrop-blur-md border border-white/10 flex-1 min-w-0">
              <code className="font-mono text-xs truncate text-blue-200 flex-1 select-all">
                {referralLink}
              </code>
              <button 
                onClick={copyLink} 
                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-bold text-xs uppercase hover:bg-blue-50 transition-all flex-shrink-0 flex items-center gap-2"
              >
                {copied ? <SafeIcon icon={FiCheckCircle} /> : <SafeIcon icon={FiCopy} />}
                {copied ? 'Kopiert' : 'Kopier'}
              </button>
            </div>
            
            <button 
              onClick={shareEmail}
              className="bg-blue-400/20 hover:bg-blue-400/30 border border-white/20 text-white px-5 py-2 rounded-xl font-bold text-xs uppercase transition-all flex-shrink-0 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <SafeIcon icon={FiMail} /> Send E-post
            </button>
          </div>
        </div>

        {/* ID BOX - Fikset posisjonering for å unngå overlapping */}
        <div className="bg-white/10 p-6 rounded-2xl backdrop-blur-xl border border-white/10 flex flex-col justify-center items-center lg:items-end min-w-[180px] shadow-inner">
          <p className="text-blue-200 text-[10px] font-black uppercase tracking-widest mb-1">{label}</p>
          <p className="font-mono text-3xl font-black text-white tracking-tighter">
            {displayId}
          </p>
          <div className="mt-3 flex items-center gap-1.5">
             <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
             <span className="text-[9px] font-bold text-blue-300 uppercase tracking-wider">Verifisert</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReferralBanner;